import { T, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  CameraIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export type LegacyScreenshotState =
  | { status: 'capturing' }
  | { status: 'done'; dataUrl: string }
  | { status: 'failed'; error: string };

/** data: URLs are blocked in new tabs by Chrome — open a blob URL instead. */
function openDataUrl(dataUrl: string) {
  void fetch(dataUrl)
    .then((response) => response.blob())
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const tab = window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
      tab?.focus();
    });
}

function ScreenshotThumb({
  screenshot,
  dimmed,
}: {
  screenshot: LegacyScreenshotState;
  dimmed: boolean;
}) {
  const gt = useGT();
  if (screenshot.status === 'capturing') {
    return (
      <div className="shrink-0 h-16 w-24 rounded border bg-muted flex items-center justify-center">
        <SpinnerIcon className="h-5 w-5 text-muted-foreground animate-spin" />
      </div>
    );
  }
  if (screenshot.status === 'failed') {
    return (
      <div className="shrink-0 h-16 w-24 rounded border bg-muted flex items-center justify-center">
        <CameraIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => openDataUrl(screenshot.dataUrl)}
      className="shrink-0 group relative cursor-pointer"
      title={gt('Open screenshot in new tab', {
        context: 'tooltip on the screenshot thumbnail in the feedback dialog',
      })}
    >
      <img
        src={screenshot.dataUrl}
        alt={gt('Page screenshot', {
          context: 'alt text for screenshot thumbnail in feedback dialog',
        })}
        className={`h-16 w-24 rounded border object-cover transition-opacity ${dimmed ? 'opacity-40' : ''}`}
      />
      <span className="absolute inset-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
        <ArrowSquareOutIcon className="h-4 w-4 text-white" weight="bold" />
      </span>
    </button>
  );
}

function ScreenshotCaption({
  screenshot,
}: {
  screenshot: LegacyScreenshotState;
}) {
  if (screenshot.status === 'capturing') {
    return (
      <p className="text-xs text-muted-foreground">
        <T context="message shown while screenshot is being captured in the feedback dialog">
          Capturing screenshot…
        </T>
      </p>
    );
  }
  return (
    <>
      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <CameraIcon className="h-3.5 w-3.5 shrink-0" weight="fill" />
        {screenshot.status === 'done' ? (
          <T context="heading shown when a screenshot was successfully captured">
            Screenshot captured
          </T>
        ) : (
          <T context="heading shown when screenshot capture failed">
            Screenshot unavailable
          </T>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {screenshot.status === 'done' ? (
          <T context="info message in feedback dialog explaining screenshot will be sent to support">
            A screenshot of your current page will be shared with the support
            team to help diagnose the issue.
          </T>
        ) : (
          <T context="info message in feedback dialog when screenshot capture failed">
            Could not capture a screenshot automatically.
          </T>
        )}
      </p>
      {screenshot.status === 'failed' && (
        <p className="text-xs text-destructive font-mono break-all">
          {screenshot.error}
        </p>
      )}
    </>
  );
}

/**
 * The original email-only "Send Feedback" modal, shown while the
 * `feedback-triage-enabled` flag is off. Presentational: state lives in
 * LegacyFeedbackButton.
 */
export function LegacyFeedbackDialog({
  open,
  submitting,
  screenshot,
  includeScreenshot,
  onIncludeScreenshotChange,
  message,
  onMessageChange,
  onClose,
  onSubmit,
  textareaRef,
}: {
  open: boolean;
  submitting: boolean;
  screenshot: LegacyScreenshotState;
  includeScreenshot: boolean;
  onIncludeScreenshotChange: (include: boolean) => void;
  message: string;
  onMessageChange: (message: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const gt = useGT();
  const canSubmit =
    message.trim().length > 0 &&
    !submitting &&
    screenshot.status !== 'capturing';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <T context="feedback dialog title">Send Feedback</T>
          </DialogTitle>
          <DialogDescription>
            <T context="feedback dialog subtitle">
              Report an issue or share feedback with the support team.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="feedback-message" className="mb-1">
              <T context="label above feedback text input">
                What&apos;s the issue or feedback?
              </T>
            </Label>
            <Textarea
              id="feedback-message"
              ref={textareaRef}
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              placeholder={gt(
                "Describe what happened or what you'd like to see improved…",
                { context: 'placeholder text in the feedback textarea' },
              )}
              rows={4}
              disabled={submitting}
            />
          </div>

          <div className="rounded-md border bg-muted/40 p-3 flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <ScreenshotThumb
                screenshot={screenshot}
                dimmed={!includeScreenshot}
              />
              <div className="flex flex-col gap-1 min-w-0">
                <ScreenshotCaption screenshot={screenshot} />
              </div>
            </div>
            {screenshot.status === 'done' && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  id="include-screenshot"
                  checked={includeScreenshot}
                  onCheckedChange={(checked) =>
                    onIncludeScreenshotChange(checked === true)
                  }
                  disabled={submitting}
                />
                <span className="text-xs text-muted-foreground">
                  <T context="checkbox label to opt out of including a screenshot in the feedback report">
                    Include screenshot with this report
                  </T>
                </span>
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            <T context="cancel button in feedback dialog">Cancel</T>
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {submitting ? (
              <T context="feedback submit button label while sending is in progress">
                Sending…
              </T>
            ) : (
              <T context="feedback submit button label">Send feedback</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
